<template>
  <div>
    <p class="auth-menu-title text-left">{{ t('title') }}</p>
    <q-select
      v-model="searchType"
      :options="searchOptions"
      :label="t('optionsLabel')"
      style="min-width:400px"
    />
    <q-input
      v-if="isSelected('layer')"
      :label="t('layerSearchLabel')"
      v-model="searchValue"
    />
    <div v-if="isSelected('coords')" class="row q-gutter-md">
      <q-select
        v-model="epsg"
        :options="epsgOptions"
        option-value="code"
        :label="t('epsgLabel')"
        class="col"
      />
      <q-input
        :label="xLabel"
        v-model="xCoord"
        class="col"
      />
      <q-input
        :label="yLabel"
        v-model="yCoord"
        class="col"
      />
    </div>
    <div v-if="isSelected('address') || isSelected('addressICGC')">
      <q-select
        v-if="hasICGCGeocoder && !hasICGCGeocoderMunicipality"
        ref="search_input"
        class="search-bar"
        :label="t('municipality')"
        fill-input
        use-input
        map-options
        hide-selected
        autofocus
        hide-dropdown-icon
        :value="municipality"
        :options="municipalityOptions"
        :input-debounce="100"
        @input="setMunicipality"
        @filter="setMunicipalityOptions"
        @keyup.enter="setMunicipalityEnter"
      />
      <div class="row q-gutter-md">
        <q-input
          :label="t('street')"
          v-model="street"
          class="col"
          style="flex: 3"
        />
        <q-input
          :label="t('streetNumber')"
          v-model="streetNumber"
          class="col"
          style="flex: 1"
        />
      </div>
    </div>
    <div v-if="isSelected('pk') && $config.geocodificadorICGC">
      <q-input
        :label="t('roadSearchLabel')"
        v-model="road"
      />
      <q-input
        :label="t('pkSearchLabel')"
        v-model="kilometer"
      />
    </div>
    <div v-if="isSelected('pkFerroviari') && $config.pkFerroviariICGC">
      <q-select
        v-model="pkFerroviariSearchType"
        :options="pkFerroviariOptions"
        :label="t('optionsLabel')"
        style="min-width:400px"
      />
      <q-input
        :label="t('pkSearchLabel')"
        v-model="kilometer"
      />
    </div>
    <div v-if="isSelected('refcad')">
      <q-input
        :label="t('refcad')"
        v-model="refcad"
        hint="La referència cadastral ha de tenir 7, 14 o 20 dígits"
        mask="NNNNNNNNNNNNNNNNNNNN"
      />
    </div>
    <q-btn
      :disabled="!searchButtonEnabled"
      color="grey-9"
      outline
      no-caps
      style="width: 100%"
      class="q-mt-md"
      :label="t('search')"
      @click="search"
    />
  </div>
</template>

<script>
import axios from 'axios'
import proj4 from 'proj4'
import { QBtn, QInput, QSelect } from 'quasar'

import { projections } from 'src/lib/projections'

export default {
  data () {
    return {
      searchType: null,
      searchValue: null,
      epsg: this.getDefaultEPSG(),
      yCoord: null,
      xCoord: null,
      municipality: null,
      municipalityOptions: [],
      street: null,
      streetNumber: null,
      road: null,
      kilometer: null,
      refcad: null,
      pkFerroviariSearchType: { label: 'PK fites', value: 'real' }
    }
  },
  components: {
    QBtn,
    QInput,
    QSelect
  },
  mounted () {
    if (this.$router.currentRoute.name === 'geovideo') {
      if (this.hasICGCGeocoderFerroviari) {
        this.searchType = { value: 'pkFerroviari', label: this.t('pkFerroviariICGC') }
      }
    }
  },
  computed: {
    searchOptions () {
      let options = []
      if (this.$config.advancedSearchConfig.searchCatalog) {
        options.push({ value: 'layer', label: this.t('layers') })
      }
      options.push({ value: 'coords', label: this.t('coordinates') })
      if (this.hasGeoportalAddressSearch) {
        options.push({ value: 'address', label: this.t('address') })
      }
      if (this.hasICGCGeocoder) {
        options.push({ value: 'addressICGC', label: this.t('addressICGC') })
        options.push({ value: 'pk', label: this.t('pkICGC') })
      }
      if (this.hasICGCGeocoderFerroviari) {
        options.push({ value: 'pkFerroviari', label: this.t('pkFerroviariICGC') })
      }
      if (this.hasGeoportalRefcadSearch) {
        options.push({ value: 'refcad', label: this.t('refcad') })
      }
      return options
    },
    hasGeoportalAddressSearch () {
      const searches = this.$config.searches.filter(search => 'address' in search)
      return searches.length > 0
    },
    hasGeoportalRefcadSearch () {
      const searches = this.$config.searches.filter(search => 'refcad' in search)
      return searches.length > 0
    },
    hasICGCGeocoder () {
      return 'geocodificadorICGC' in this.$config && this.$config.geocodificadorICGC
    },
    hasICGCGeocoderMunicipality () {
      return this.hasICGCGeocoder && 'mun' in this.$config.geocodificadorICGC.params
    },
    hasICGCGeocoderFerroviari () {
      return 'pkFerroviariICGC' in this.$config && Object.entries(this.$config.pkFerroviariICGC).length > 0
    },
    searchButtonEnabled () {
      const layerValueOption = this.isSelected('layer')
      const layerValueOptionReady = layerValueOption && this.searchValue
      const coordValuesReady = this.isSelected('coords') && this.epsg && this.xCoord && this.yCoord
      const addressOption = this.isSelected('address') || this.isSelected('addressICGC')
      const addressValuesReady = addressOption && this.street && this.streetNumber
      const pkValuesReady = this.isSelected('pk') && this.road && this.kilometer
      const pkFerroviariValuesReady = this.isSelected('pkFerroviari') && this.kilometer
      const refcadValuesReady = this.isSelected('refcad') && (this.refcad?.length === 7 || this.refcad?.length === 14 || this.refcad?.length === 20)
      return layerValueOptionReady || coordValuesReady || addressValuesReady || pkValuesReady || pkFerroviariValuesReady || refcadValuesReady
    },
    epsgOptions () {
      return this.$config.epsgs || []
    },
    isLatLon () {
      return this.epsg && this.epsg.code === 'EPSG:4326'
    },
    yLabel () {
      return this.isLatLon ? this.t('latitude') : this.t('y')
    },
    xLabel () {
      return this.isLatLon ? this.t('longitude') : this.t('x')
    },
    pkFerroviariOptions () {
      return Object.entries(this.$config.pkFerroviariICGC).map(item => {
        return {
          label: item[1].title,
          value: item[0]
        }
      })
    }
  },
  methods: {
    t (key, ...args) {
      return this.$t('tools.search.advanced.' + key, ...args)
    },
    getDefaultEPSG () {
      return this.$config.epsgs ? this.$config.epsgs[0] : {}
    },
    isSelected (searchType) {
      return this.searchType && this.searchType.value === searchType
    },
    setSearchValueFromCoords () {
      if (this.epsg && this.epsg.code !== 'EPSG:4326') {
        proj4.defs(projections)
        const latLng = proj4(this.epsg.code, 'EPSG:4326').forward([parseFloat(this.xCoord), parseFloat(this.yCoord)])
        this.yCoord = latLng[1]
        this.xCoord = latLng[0]
      }
      this.searchValue = `${this.yCoord}, ${this.xCoord}`
    },
    setSearchValueFromAddress () {
      this.searchValue = `${this.street} ${this.streetNumber}`
      if (this.searchType.value === 'addressICGC' && this.municipality) {
        this.searchValue += `, ${this.municipality}`
      }
    },
    setSearchValueFromPK () {
      this.searchValue = `${this.road} ${this.kilometer}`
    },
    setSearchValueFromPKFerroviari () {
      this.searchValue = `${this.pkFerroviariSearchType.value}=${this.kilometer}`
    },
    setSearchValueFromRefcad () {
      this.searchValue = this.refcad
    },
    search () {
      if (this.searchType.value === 'coords') {
        this.setSearchValueFromCoords()
      } else if (['address', 'addressICGC'].includes(this.searchType.value)) {
        this.setSearchValueFromAddress()
      } else if (this.searchType.value === 'pk') {
        this.setSearchValueFromPK()
      } else if (this.searchType.value === 'pkFerroviari') {
        this.setSearchValueFromPKFerroviari()
      } else if (this.searchType.value === 'refcad') {
        this.setSearchValueFromRefcad()
      }
      this.$emit('search', {
        option: this.searchType.value,
        input: this.searchValue
      })
    },
    setMunicipalityOptions (input, update, abort) {
      update(() => {
        if (!input) {
          return
        }
        this.getMunicipalityOptions(input)
      })
    },
    setMunicipalityEnter () {
      if (this.municipality instanceof String || typeof this.municipality === 'string') {
        this.getMunicipalityOptions(this.municipality)
      } else {
        this.setMunicipality(this.municipality)
      }
    },
    getMunicipalityOptions (input) {
      const needle = input.toLocaleLowerCase()
      if (needle !== '') {
        const params = {
          text: needle,
          size: 8,
          layers: 'topo_tipus_01'
        }
        axios.get(this.$config.geocodificadorICGC.url, { params }).then(result => {
          if (result?.data?.features?.length > 0) {
            this.municipalityOptions = result.data.features.map(feature => feature.properties.nom)
          } else {
            this.municipalityOptions = []
          }
        })
      }
    },
    setMunicipality (value) {
      this.municipality = value
    }
  }
}
</script>
